<template>
  <div class="tournament" v-if="myTournament" v-show="showFloating">
    <p
      class="notification is-danger"
      v-show="showMessage && myTournament && myTournament.message"
    >
      <button class="delete" @click="showMessage = false"></button>
      {{ myTournament.message }}
    </p>

    <b-collapse class="card" animation="slide" :open="true">
      <template #trigger="props">
        <header class="card-header">
          <div class="card-header-title">
            <p
              :style="`max-width: ${
                props.open ? '450px' : '200px'
              }; white-space: nowrap; overflow: hidden;`"
            >
              {{ myTournament.name }}
              <span
                v-if="myTournament.recurrence && myTournament.recurrence.repeat"
              >
                {{
                  myTournament.startTime
                    | asDayjsFormat(
                      `DD/MM/YYYY${
                        myTournament.recurrence.repeat.basis === "minute"
                          ? " HH:mm"
                          : ""
                      }`
                    )
                }}
              </span>
            </p>
          </div>

          <p class="card-header-icon">
            <span>{{ myTournament.startTime | asTime }}</span>
            &nbsp;
            <span>
              <div class="control">
                <div class="tags has-addons">
                  <span class="tag is-light">Round</span>
                  <span class="tag is-info">
                    {{ myTournament.currentRound }}/{{
                      myTournament.totalRounds
                    }}
                  </span>
                </div>
              </div>
            </span>
            <b-icon :icon="props.open ? 'caret-down' : 'caret-up'"></b-icon>
          </p>
        </header>
      </template>

      <div class="card-content">
        <!-- Player info -->
        <div class="body-section">
          <div
            v-if="myPlayer"
            class="is-flex is-justify-content-space-evenly is-align-items-center"
          >
            <div v-if="needCheckIn">
              <button class="button is-warning" @click="participate">
                Check in for round {{ myTournament.currentRound + 1 }}
              </button>
            </div>
            <router-link
              v-else-if="!myNextGame && this.$route.name !== 'tournament'"
              :to="`/tournaments/${myTournament.id}`"
            >
              <button class="button is-success">Go to the tournament</button>
            </router-link>

            <RoundResults
              :roundsParticipated="myPlayer.roundsParticipated"
              :currentRoundNumber="myTournament.currentRound"
              :totalRounds="myTournament.totalRounds"
              :username="myPlayer.username"
              :games="myGames"
              class="ml-2"
            />
            <div class="control ml-2">
              <div class="tags has-addons">
                <span class="tag is-medium is-light">Total Score</span>
                <span class="tag is-medium is-primary">
                  {{ myPlayer.score }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Current game info -->
        <div v-if="myNextGame" class="body-section">
          <div
            class="is-flex is-justify-content-space-between is-align-items-center"
          >
            <span v-if="myCurrentMatch">
              <router-link
                :to="
                  getTournamentGameUrl(myTournament.id, myCurrentMatch.match_id)
                "
              >
                <button class="button is-danger">Play</button>
              </router-link>
            </span>
            <span v-else>
              <p>Your</p>
              Next game:
            </span>
            <span>
              <p>Round</p>
              <span class="tag is-primary">{{ myNextGame.roundNumber }}</span>
            </span>
            <span>
              <p>Board</p>
              <span class="tag is-success">{{ myNextGame.board }}</span>
            </span>
            <ProfileCard
              :player="myPlayer.user"
              color="white"
              v-if="myPlayer"
            />
            <span>vs.</span>
            <ProfileCard
              :player="myOpponent.user"
              color="white"
              v-if="myOpponent"
            />
            <td>
              <p>{{ myNextGame.startTime | asTime }}</p>
              <p>{{ myNextGame.finishTime | asTime }}</p>
            </td>
          </div>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import ProfileCard from "@/components/players/ProfileCard.vue";
import RoundResults from "@/components/tournaments/RoundResults.vue";
import gameUrls from "@/mixins/gameUrls";
import { firestore } from "@/services/firebase";
import { TOURNAMENT_GAME_RESULT } from "@shared/constants";
import { isPlayerInRound, participate } from "@shared/tournament/helper";
import { mapGetters, mapMutations } from "vuex";

export default {
  components: { ProfileCard, RoundResults },
  name: "TournamentFloating",

  mixins: [gameUrls],

  data() {
    return {
      tournaments: null,
      myPlayer: null,
      myOpponent: null,
      games: null,
      showMessage: true,
    };
  },

  computed: {
    ...mapGetters(["getPlayer", "getOnlineStatus"]),

    showFloating() {
      return !(
        (this.$route.name === "tournamentGame" ||
          this.$route.name === "tournamentGameV2") &&
        this.myNextGame &&
        this.$route.params.gameId === this.myNextGame.matchGUID
      );
    },

    activeTournaments() {
      if (!this.tournaments || !this.tournaments.length) return [];
      return this.tournaments.filter((t) => t.currentRound <= t.totalRounds);
    },

    myTournament() {
      if (!this.activeTournaments || !this.activeTournaments.length) {
        return null;
      }
      if (!this.getPlayer) return null;
      return this.activeTournaments.find(
        (t) =>
          t.playerUsernames &&
          t.playerUsernames.includes(this.getPlayer.username) &&
          t.startTime.toDate().getTime() - Date.now() < 12 * 60 * 60 * 1000 // show in the date only
      );
    },

    myGames() {
      if (!this.games || !this.games.length) return [];
      if (!this.getPlayer) return [];
      return this.games.filter(
        (g) =>
          g.whiteUsername === this.getPlayer.username ||
          g.blackUsername === this.getPlayer.username
      );
    },

    myNextGame() {
      return this.myGames.find(
        (g) =>
          g.roundNumber >= this.myTournament.currentRound &&
          !g.finishTime &&
          g.whiteResult === TOURNAMENT_GAME_RESULT.UNFINISHED
      );
    },

    myCurrentMatch() {
      if (!this.myNextGame) return null;
      return this.myNextGame.matchRef;
    },

    opponentUsername() {
      if (!this.myNextGame) return null;
      if (!this.myPlayer) return null;
      if (this.myNextGame.whiteUsername === this.myPlayer.username) {
        return this.myNextGame.blackUsername;
      }
      if (this.myNextGame.blackUsername === this.myPlayer.username) {
        return this.myNextGame.whiteUsername;
      }
      return null;
    },

    isInTournament() {
      if (this.myTournament == null) return false;
      if (!this.myPlayer) return false;
      if (this.myTournament.currentRound === 0) {
        const ticksUntilStart =
          this.myTournament.startTime.toDate().getTime() - Date.now();
        console.log("ticksUntilStart", ticksUntilStart);
        return ticksUntilStart < 3600 * 1000;
      }
      return this.myTournament.currentRound <= this.myTournament.totalRounds;
    },

    needCheckIn() {
      if (!this.isInTournament) return null;
      if (this.myTournament.currentRound >= this.myTournament.totalRounds) {
        return false;
      }
      return !isPlayerInRound(this.myPlayer, this.myTournament.currentRound);
    },
  },

  firestore: {
    tournaments: firestore
      .collection("tournaments")
      .where("isDeleted", "==", false),
  },

  watch: {
    activeTournaments: {
      immediate: true,
      handler() {
        if (!this.getPlayer) return;
        this.updateActiveTournaments();
      },
    },

    getPlayer: {
      immediate: true,
      handler() {
        if (!this.getPlayer) return;
        this.updateActiveTournaments();
      },
    },

    myTournament: {
      immediate: true,
      handler(myTournament) {
        if (!myTournament && this.myPlayer) {
          this.$unbind("myPlayer");
          this.$unbind("games");
          return;
        }
        if (!myTournament) return;
        this.showMessage = true;
        const tournamentRef = firestore
          .collection("tournaments")
          .doc(myTournament.id);
        this.$bind(
          "myPlayer",
          tournamentRef.collection("players").doc(this.getPlayer.username)
        );
        this.$bind("games", tournamentRef.collection("games"));
      },
    },

    isInTournament: {
      immediate: true,
      handler() {
        if (!this.isInTournament) {
          return;
        }

        this.participate();
      },
    },

    opponentUsername: {
      immediate: true,
      handler() {
        if (!this.opponentUsername) {
          if (this.myOpponent) this.$unbind("myOpponent");
          return;
        }

        this.$bind(
          "myOpponent",
          firestore
            .collection("tournaments")
            .doc(this.myTournament.id)
            .collection("players")
            .doc(this.opponentUsername)
        );
      },
    },

    myCurrentMatch: {
      immediate: true,
      handler() {
        if (
          !this.myCurrentMatch ||
          !this.myCurrentMatch.startedAt ||
          !this.myCurrentMatch.match_id
        )
          return;
        if (
          this.$route.name !== "tournamentGame" &&
          this.$route.name !== "tournamentGameV2"
        ) {
          this.$router.push({
            path: this.getTournamentGameUrl(
              this.myTournament.id,
              this.myCurrentMatch.match_id
            ),
          });
        }
      },
    },
  },

  methods: {
    ...mapMutations(["setActiveTournaments"]),

    participate() {
      participate(
        firestore,
        this.myPlayer,
        this.myTournament,
        null,
        this.getPlayer // update user profile in tournament
      );
    },

    updateActiveTournaments() {
      this.setActiveTournaments(
        this.activeTournaments
          .filter(
            (t) =>
              t.privacy !== "Private" ||
              (t.playerUsernames &&
                this.getPlayer &&
                t.playerUsernames.includes(this.getPlayer.username))
          )
          .sort((a, b) => a.startTime - b.startTime)
      );
    },
  },
};
</script>

<style scoped>
.tournament {
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 1000;
  /* max-width: 600px; */
}

.card-header {
  background-color: darkslateblue; /*  #29c82d; */
  color: whitesmoke;
}

.card-header-title {
  color: whitesmoke;
}

.card-content {
  background-color: lightgray;
  color: whitesmoke;
  padding: 0;
}

.body-section {
  background-color: darkblue;
  padding: 1rem;
  margin: 0;
  border-top: 2px solid darkgray; /* Light gray border */
}
</style>
